import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    styled,
    alpha,
    Button,
    Typography,
    TextField,
    List,
    Paper,
    Popper,
    Divider,
    useMediaQuery,
    ClickAwayListener,
    FormControlLabel,
    Switch,
    LinearProgress, // wrap
} from '@mui/material';
import { Close } from '@mui/icons-material';
import LocalizationContext from '../../../../../localization/LocalizationContext';
import { NotificationContext } from '../../../../../NotificationContext';
import jwtService from 'app/services/jwtService';
import useAccessList from './SwitchUserButton.queries';
import { setUserData } from '../../../../../auth/store/actions/user.actions';
import { getInitialRoute } from '../../../../../../@fuse/components/FuseAuthorization/FuseAuthorization';
import { getUserIcon, addRecentItem, getRecentItems, renderOptions, getMainUserOption } from './SwitchUserButton.helpers';

const ButtonWrap = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textTransform: 'none',
    height: '50px',
    minWidth: isMobile ? '185px' : '250px',
    maxWidth: isMobile ? '185px' : null,
    color: theme.palette.secondary.main,
    borderColor: alpha(theme.palette.secondary.main, 0.12),
    '&:hover': {
        color: theme.palette.primary.main,
    },
    '& .MuiButton-startIcon svg': {
        width: '28px',
        height: '28px',
        padding: '3px',
        borderRadius: '2.5px',
        color: theme.palette.nav.text.med,
        backgroundColor: theme.palette.background.main,
    },
    '& .MuiButton-endIcon': {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.secondary.main,
    },
}));

const RecentSearch = styled('div')(({ theme }) => ({
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '120%',
    color: alpha(theme.palette.secondary.main, 0.7),
    marginLeft: theme.spacing(1.5),
}));

const StyledEllipsisText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'block',
    maxWidth: isMobile ? '125px' : '200px',
}));

const RECENTS_KEY = 'impersonation_recent_user_ids';
const errorMessage = 'There was an error switching users. Contact support if this issue persists.';

const SwitchUserButton = ({ user }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const history = useHistory();
    const localization = useContext(LocalizationContext);
    const { showSnackbar } = useContext(NotificationContext);

    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');
    const [hideInactive, setHideInactive] = useState(true);

    // Get parent name from sessionStorage (set during login)
    const parentName = sessionStorage.getItem('user_parent_name');

    const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
    const canImpersonate = sessionStorage.getItem('impersonationAllowed') === 'true';
    const isImpersonating = sessionStorage.getItem('isImpersonating') === 'true';

    const { data: users, isLoading } = useAccessList(user.id, user.role, hideInactive);
    const accessOptions = users?.accesses ?? [];
    const mainUserOption = getMainUserOption(user, isImpersonating, parentName);

    const options = [...(mainUserOption ? [mainUserOption] : []), ...accessOptions.filter((opt) => opt.id !== mainUserOption?.id)];

    const hasAccessableOptions = accessOptions && accessOptions.length > 0;
    const grantImpersonation = isAdmin ? canImpersonate : hasAccessableOptions;

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', height: 50, width: isMobile ? 185 : 250 }}>
                <LinearProgress sx={{ width: '100%', height: 4, borderRadius: 2 }} />
            </div>
        );
    }

    if (!grantImpersonation && !isImpersonating) return null;

    const open = Boolean(anchorEl);
    const recentUserIds = getRecentItems(RECENTS_KEY);
    const recentOptions = recentUserIds
        .map((id) => accessOptions.find((opt) => opt.id === id)) // wrap
        .filter(Boolean);

    const filteredOptions = options.filter((opt) => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        const matchesLabel = opt.label?.toLowerCase().includes(searchLower);
        const matchesLicense = opt.license_number?.toLowerCase().includes(searchLower);
        const matchesHauler = opt.hauler_number?.toLowerCase().includes(searchLower);
        const matchesMain = opt.isMainUser && 'main'.includes(searchLower);
        return matchesLabel || matchesLicense || matchesHauler || matchesMain;
    });

    const handleToggle = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleChangeUser = async (option) => {
        const { id: newUserId, isMainUser } = option;

        // If clicking on main user option while impersonating, return to main
        if (isMainUser && isImpersonating) {
            return handleReturnToMain();
        }

        // If clicking on current user, do nothing
        if (newUserId === user.id) return;

        setAnchorEl(null);
        addRecentItem(RECENTS_KEY, newUserId);

        try {
            const mainUser = !isImpersonating
                ? {
                      id: user.id,
                      name: user.name,
                      email: user.email,
                      role: user.role,
                      subuser: user.subuser,
                      producer_id: user.producer_id,
                      processor_id: user.processor_id,
                      hauling_id: user.hauling_id,
                  }
                : null;
            const impersonatedUser = await jwtService.signInAsImpersonatedUser(newUserId, mainUser);
            dispatch(setUserData(impersonatedUser));

            history.push({
                pathname: impersonatedUser.data.name === 'DFNS' ? '/dropoffs' : getInitialRoute(impersonatedUser.data),
            });
            setTimeout(() => window.location.reload(), 50);
        } catch {
            showSnackbar({
                message: errorMessage,
                severity: 'error',
            });
        }
    };

    const handleReturnToMain = async () => {
        setAnchorEl(null);

        try {
            const restoredUser = await jwtService.revertImpersonatedLogin();
            dispatch(setUserData(restoredUser));

            history.push({ pathname: getInitialRoute(restoredUser.data) });
            setTimeout(() => window.location.reload(), 50);
        } catch {
            showSnackbar({
                message: errorMessage,
                severity: 'error',
            });
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <div>
                <ButtonWrap
                    variant="outlined"
                    startIcon={getUserIcon(isImpersonating, user?.role)}
                    endIcon={
                        isImpersonating ? (
                            <Close
                                fontSize="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleReturnToMain();
                                }}
                            />
                        ) : null
                    }
                    onClick={handleToggle}
                    isMobile={isMobile}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            lineHeight: 1,
                        }}
                    >
                        <StyledEllipsisText
                            isMobile={isMobile}
                            variant="body2"
                            fontWeight="bold"
                            color={isImpersonating ? 'primary' : 'secondary'}
                            sx={{
                                fontSize: isMobile ? '1.3rem' : '1.4rem',
                            }}
                        >
                            {isImpersonating ? `${user.name}` : 'Switch User'}
                        </StyledEllipsisText>
                        <StyledEllipsisText
                            isMobile={isMobile}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontSize: isMobile ? '1.1rem' : '1.3rem',
                            }}
                        >
                            {user.email}
                        </StyledEllipsisText>
                    </div>
                </ButtonWrap>

                <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
                    <Paper
                        sx={{
                            width: 480,
                            maxHeight: 520,
                            p: 1,
                            overflow: 'auto',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                margin: '0 4px',
                            }}
                        >
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ width: isImpersonating ? '300px' : '100%' }} // wrap
                            />
                            {isImpersonating && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleReturnToMain}
                                    sx={{ width: '150px' }} // wrap
                                >
                                    Back to Main
                                </Button>
                            )}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={hideInactive}
                                        onChange={(e) => setHideInactive(e.target.checked)} // wrap
                                    />
                                }
                                label="Hide Inactive"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        lineHeight: '120%',
                                        color: (theme) => alpha(theme.palette.secondary.main, 0.7),
                                    },
                                }}
                            />
                        </div>
                        <List sx={{ paddingTop: 0 }}>
                            {recentOptions.length > 0 && !search && (
                                <>
                                    <RecentSearch>Recent Search</RecentSearch>
                                    {renderOptions(recentOptions, handleChangeUser, localization, user.id)}
                                    <Divider sx={{ my: 1 }} />
                                </>
                            )}
                            {renderOptions(filteredOptions, handleChangeUser, localization, user.id)}
                        </List>
                    </Paper>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

export default SwitchUserButton;
